import { AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BlogQualityIssue, BlogQualityReport } from '../lib/blogQualityChecks';

const DETAIL_LIMIT = 12;

function IssueList({
    items,
    tone,
    onRewriteBlobImages,
}: {
    items: BlogQualityIssue[];
    tone: 'blocking' | 'warning';
    onRewriteBlobImages?: () => void;
}) {
    if (items.length === 0) return null;
    const blocking = tone === 'blocking';
    return (
        <div className="space-y-2">
            <p className={cn(
                'text-[11px] font-semibold uppercase tracking-wide',
                blocking ? 'text-destructive' : 'text-amber-800 dark:text-amber-300',
            )}
            >
                {blocking ? 'Bloqueantes' : 'Avisos'}
            </p>
            <ul className="space-y-2">
                {items.map((item) => {
                    const hidden = Math.max(0, item.details.length - DETAIL_LIMIT);
                    return (
                        <li
                            key={item.rule}
                            data-rule={item.rule}
                            className={cn(
                                'rounded-md border px-3 py-2 text-xs',
                                blocking
                                    ? 'border-destructive/30 bg-destructive/5'
                                    : 'border-amber-500/30 bg-amber-500/10',
                            )}
                        >
                            <p className="font-medium">{item.message}</p>
                            {item.details.length > 0 && (
                                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-muted-foreground">
                                    {item.details.slice(0, DETAIL_LIMIT).map((detail) => (
                                        <li key={detail}>{detail}</li>
                                    ))}
                                    {hidden > 0 && <li>y {hidden} más</li>}
                                </ul>
                            )}
                            {item.rule === 'cdn_images' && onRewriteBlobImages && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="mt-2 h-7 text-xs"
                                    onClick={onRewriteBlobImages}
                                >
                                    Reescribir a dominio blob
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export function BlogQualityPanel({
    report,
    checkingLinks,
    publishArmed,
    onRewriteBlobImages,
}: {
    report: BlogQualityReport;
    checkingLinks: boolean;
    publishArmed: boolean;
    onRewriteBlobImages: () => void;
}) {
    const quiet = report.issues.length === 0;
    return (
        <section
            id="blog-quality-panel"
            aria-label="Calidad del artículo"
            data-blog-quality={report.blocking.length > 0 ? 'blocking' : report.warnings.length > 0 ? 'warning' : 'ok'}
            className="w-full rounded-lg border border-border bg-card/40 px-3 py-2 space-y-2"
        >
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {report.blocking.length > 0
                    ? <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    : <Info className="h-3.5 w-3.5" />}
                <span>
                    Los bloqueantes piden confirmación al publicar. Los avisos no impiden publicar.
                </span>
                {checkingLinks && <span>Comprobando enlaces internos…</span>}
            </div>
            {quiet && !checkingLinks && (
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Sin avisos de calidad.</p>
            )}
            <IssueList items={report.blocking} tone="blocking" />
            <IssueList items={report.warnings} tone="warning" onRewriteBlobImages={onRewriteBlobImages} />
            {publishArmed && report.blocking.length > 0 && (
                <p className="text-xs font-medium text-destructive" role="status">
                    Pulsa «Publicar igualmente» para publicar dejando constancia en la consola.
                </p>
            )}
        </section>
    );
}
